// Rebalancing API Service
// Handles all API calls for the rebalancing functionality

import { apiClient } from "@/services/api";
import { getAvailableExposures } from './widget';
import { fetchTaskResultWithRetry, type RebalancingTaskResponse } from "@/utils/rebalancingTasks";

export type { RebalancingTaskResponse };

// Backend-aligned interfaces matching actual API schemas

export interface Portfolio {
  id: string;
  portfolio_id: string;
  name: string;
  type: string;
  asAtDate: string;
  holdingsCount: number;
  totalValue?: number;
  currency?: string;
  manager?: string;
  category?: string;
}

export interface ProductLookupInfo {
  isin: string;
  currency: string;
  long_name: string;
  saltus_sub_asset_allocation?: string;
  subscription_notice_period?: string;
  redemption_notice_period?: string;
  settlement_offset_days?: string;
  trading_day_definition?: string;
}

export interface HoldingWeightResponse {
  product_id: string;
  current_weight: number;
  target_weight: number;
  model_weight: number;
  product_name?: string;
  drift_percentage?: number; // current - model
  resource_type?: string; // "product" or "portfolio"
  isin?: string; // ISIN identifier
  currency?: string; // Base currency
}

export interface PortfolioSelectionResponse {
  portfolio_id: string;
  portfolio_name: string;
  display_order: number;
  is_active: boolean;
  holdings: HoldingWeightResponse[];  // Each portfolio has its own holdings

  // Comparison model support
  comparison_portfolio_id?: string;       // Selected comparison portfolio
  comparison_portfolio_name?: string;     // Name for display
  comparison_holdings?: HoldingWeightResponse[];  // Model weights from comparison
  comparison_lookthrough?: boolean;       // Whether to expand nested portfolios
}

export interface ValidationResultResponse {
  valid: boolean;
  error?: string;
  warnings: string[];
}

export interface CalculationAuditResponse {
  calculation_id: string;
  timestamp: string;
  holdings_issues: any[];
  proxy_usage: any[];
  performance_metrics: Record<string, number>;
  calculation_time_ms?: number;
}

export interface AnalysisCacheStatusResponse {
  exposures_ready: boolean;
  fundamentals_ready: boolean;
  timeseries_ready: boolean;
}

export interface AnalysisResponse {
  id: string;
  analysis_id: string;
  name: string;
  description?: string;
  created_by: string;
  created_at: string;
  updated_at: string;
  last_modified: string;

  // Portfolio and holdings
  selected_portfolios: PortfolioSelectionResponse[];

  // Nested portfolio support  
  view_mode: 'top_level' | 'underlying' | 'aggregate';
  underlying_portfolios: PortfolioSelectionResponse[];
  aggregate_portfolios: PortfolioSelectionResponse[];

  // Analysis state
  status: string;
  is_locked: boolean;

  // Validation and results
  validation_result?: ValidationResultResponse;
  last_calculation_id?: string;
  cached_results?: Record<string, any>;
  audit_info?: CalculationAuditResponse;

  // Computed fields
  total_weight: number;
  is_valid_weights: boolean;

  // Cached analytics metadata
  cached_exposure_types?: string[];
  has_underlying_portfolios?: boolean;
  cache_status?: AnalysisCacheStatusResponse;
}

export interface AnalysisSummaryResponse {
  analysis_id: string;
  name: string;
  description?: string;
  created_by: string;
  created_by_email?: string;
  created_by_name?: string;
  created_at: string;
  updated_at: string;
  status: string;
  portfolio_count?: number;
  total_holdings_count?: number;
}

// Request schemas
export interface CreateAnalysisRequest {
  name: string;
  description?: string;
}

export interface UpdateAnalysisRequest {
  name?: string;
  description?: string;
  last_modified?: string;
}

export interface HoldingWeightRequest {
  product_id: string;
  current_weight: number;
  target_weight: number;
  model_weight: number;
  product_name?: string;
}

export interface UpdateHoldingsRequest {
  holdings: HoldingWeightRequest[];
}

export interface PortfolioHoldingsRequest {
  portfolio_id: string;
  portfolio_name: string;
  display_order: number;
  is_active: boolean;
  holdings: HoldingWeightRequest[];
}

export interface UpdatePortfoliosRequest {
  portfolios: PortfolioHoldingsRequest[];
}

export interface PortfolioSelectionRequest {
  portfolio_id: string;
  portfolio_name: string;
  display_order: number;
  is_active: boolean;
}

export interface SearchFilters {
  query?: string;
  type?: string[];
  manager?: string[];
  category?: string[];
  dateRange?: { from?: string; to?: string };
  minHoldings?: number;
  maxHoldings?: number;
}

export interface SearchResponse {
  portfolios: Portfolio[];
  total: number;
  page: number;
  pageSize: number;
}

export interface ImpactCalculationResponse {
  calculation_id: string;
  results: Record<string, number | null>;
  cached: boolean;
  calculation_time_ms: number;
  cache_level: number;
  proxy_metadata: Record<string, any>;
  exposures?: Record<string, any>;
  fees?: Record<string, any>;
}

export interface CalculateImpactRequest {
  period_months: number;
}

interface ExposureVariant {
  long: Record<string, number>;
  short: Record<string, number>;
  net: Record<string, number>;
}

interface AlignedExposureData {
  model: ExposureVariant;
  current: ExposureVariant;
  target: ExposureVariant;
  diff_from_model: ExposureVariant;
  diff_from_current: ExposureVariant;
}

interface FundamentalFieldData {
  display_name: string;
  field_type: string;
  format?: string;
  model: number | string;
  current: number | string;
  target: number | string;
  diff_from_model?: number;
  diff_from_current?: number;
}

interface FundamentalImpactData {
  fields: Record<string, FundamentalFieldData>; // field_id -> field data
}

// Risk statistics data structure (metric_key -> metric data)
type RiskStatsData = Record<string, any>;

interface ComparisonData {
  portfolio_id?: string;
  portfolio_name?: string;
  exposures?: Record<string, AlignedExposureData>;
  fundamentals?: FundamentalImpactData;
  risk_stats?: RiskStatsData;
}

interface PortfolioExposureData {
  portfolio_name: string;
  exposures: Record<string, AlignedExposureData>; // exposure_type -> aligned data
  fundamentals?: FundamentalImpactData; // fundamental field impacts
  risk_stats?: RiskStatsData; // risk statistics
  comparison?: ComparisonData; // comparison portfolio data
}

export type { PortfolioExposureData };

export interface ExposureImpactResponse {
  portfolios: Record<string, PortfolioExposureData>;  // portfolio_id -> portfolio data
  exposure_types: string[];
  calculation_time: string;
  column_alignment: {
    description: string;
    columns: string[];
  };
  fundamentals_available: boolean;
}

export interface AnalysisListResponse {
  analyses: AnalysisSummaryResponse[];
  total: number;
  limit: number;
  offset: number;
}

// Product search for holdings alignment
export interface SearchProduct {
  resource_id?: string;
  child_universe_id?: string;
  portfolio_reference_id?: string;
  display_name: string;
  resource_type: string;
  company_id?: string;
  metadata?: {
    ticker?: string;
    isin?: string;
    cusip?: string;
    currency?: string;
  };
}

class RebalancingApiService {

  // Portfolio Search and Selection
  async searchPortfolios(
    filters: SearchFilters,
    page: number = 1,
    pageSize: number = 20,
    excludeIds: string[] = []
  ): Promise<SearchResponse> {
    const params = new URLSearchParams();
    if (filters.query) params.append('query', filters.query);
    if (filters.type && filters.type.length > 0) params.append('portfolio_type', filters.type[0]);
    params.append('limit', pageSize.toString());
    params.append('offset', ((page - 1) * pageSize).toString());

    const response = await apiClient.get(`/rebalancing/portfolios/search?${params.toString()}`);
    return response.data;
  }

  async getPortfolioDetails(portfolioId: string): Promise<Portfolio> {
    try {
      const response = await apiClient.get(`/rebalancing/portfolios/${portfolioId}`);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch portfolio details:', error);
      throw error;
    }
  }

  // Holdings Management - NEW for alignment functionality
  async searchProducts(portfolioId: string, query: string): Promise<SearchProduct[]> {
    try {
      const response = await apiClient.get(`/portfolios/${portfolioId}/holdings/available-products`, {
        params: { search: query }
      });
      return response.data.data || [];
    } catch (error) {
      console.error('Product search failed:', error);
      throw error;
    }
  }

  async addHolding(
    analysisId: string,
    portfolioId: string,
    product: SearchProduct
  ): Promise<void> {
    try {
      const response = await apiClient.post(`/rebalancing/analyses/${analysisId}/holdings`, {
        portfolioId,
        productId: product.resource_id || product.child_universe_id || product.portfolio_reference_id,
        productName: product.display_name,
        resourceType: product.resource_type,
        companyId: product.company_id,
        initialWeight: 0
      });
    } catch (error) {
      console.error('Failed to add holding:', error);
      throw error;
    }
  }

  async removeHolding(
    analysisId: string,
    portfolioId: string,
    holdingId: string
  ): Promise<void> {
    try {
      const response = await apiClient.delete(
        `/rebalancing/analyses/${analysisId}/portfolios/${portfolioId}/holdings/${holdingId}`
      );
    } catch (error) {
      console.error('Failed to remove holding:', error);
      throw error;
    }
  }

  // Rebalancing Analysis CRUD
  async createAnalysis(request: CreateAnalysisRequest): Promise<AnalysisResponse> {
    try {
      const response = await apiClient.post(`/rebalancing/analyses/`, request);
      return response.data;
    } catch (error) {
      console.error('Failed to create analysis:', error);
      throw error;
    }
  }



  async updateAnalysis(analysisId: string, request: UpdateAnalysisRequest): Promise<AnalysisResponse> {
    try {
      const response = await apiClient.put(`/rebalancing/analyses/${analysisId}`, request);
      return response.data;
    } catch (error) {
      console.error('Failed to update analysis:', error);
      throw error;
    }
  }

  async getAnalysis(
    analysisId: string,
    options?: { deferInactiveViewPortfolios?: boolean },
  ): Promise<AnalysisResponse> {
    try {
      const defer =
        options?.deferInactiveViewPortfolios !== undefined
          ? options.deferInactiveViewPortfolios
          : true;
      const response = await apiClient.get(`/rebalancing/analyses/${analysisId}`, {
        params: { defer_inactive_view_portfolios: defer },
      });
      return response.data;
    } catch (error) {
      console.error('Failed to fetch analysis:', error);
      throw error;
    }
  }

  async getAnalysisPortfolios(
    analysisId: string,
    viewMode: 'top_level' | 'underlying' | 'aggregate',
  ): Promise<PortfolioSelectionResponse[]> {
    try {
      const response = await apiClient.get(`/rebalancing/analyses/${analysisId}/portfolios`, {
        params: { view_mode: viewMode },
      });
      return response.data;
    } catch (error) {
      console.error('Failed to fetch analysis portfolios:', error);
      throw error;
    }
  }

  async addPortfolioSelection(
    analysisId: string,
    portfolioSelection: PortfolioSelectionRequest
  ): Promise<AnalysisResponse> {
    try {
      const response = await apiClient.post(`/rebalancing/analyses/${analysisId}/portfolios`, portfolioSelection);
      return response.data;
    } catch (error) {
      console.error('Failed to add portfolio selection:', error);
      throw error;
    }
  }

  async updateHoldings(
    analysisId: string,
    request: UpdateHoldingsRequest
  ): Promise<AnalysisResponse> {
    try {
      const response = await apiClient.put(`/rebalancing/analyses/${analysisId}/holdings`, request);
      return response.data;
    } catch (error) {
      console.error('Failed to update holdings:', error);
      throw error;
    }
  }

  async prefetchAnalysisData(analysisId: string): Promise<RebalancingTaskResponse> {
    try {
      const response = await apiClient.post(`/rebalancing/analyses/${analysisId}/prefetch`);
      return response.data;
    } catch (error) {
      console.error('Failed to prefetch analysis data:', error);
      throw error;
    }
  }

  async getPrefetchResult(analysisId: string, taskId: string): Promise<{ status: string; message: string }> {
    const response = await apiClient.get(`/rebalancing/analyses/${analysisId}/prefetch/${taskId}`);
    return response.data;
  }

  async runPrefetchAndWait(analysisId: string): Promise<{ status: string; message: string }> {
    const { task_id } = await this.prefetchAnalysisData(analysisId);
    return fetchTaskResultWithRetry((id) => this.getPrefetchResult(analysisId, id), task_id);
  }

  async refreshAnalysisData(analysisId: string): Promise<RebalancingTaskResponse> {
    try {
      const response = await apiClient.post(`/rebalancing/analyses/${analysisId}/refresh-data`);
      return response.data;
    } catch (error) {
      console.error('Failed to refresh analysis data:', error);
      throw error;
    }
  }

  async getRefreshDataResult(analysisId: string, taskId: string): Promise<{ status: string; message: string }> {
    const response = await apiClient.get(`/rebalancing/analyses/${analysisId}/refresh-data/${taskId}`);
    return response.data;
  }

  async runRefreshDataAndWait(analysisId: string): Promise<{ status: string; message: string }> {
    const { task_id } = await this.refreshAnalysisData(analysisId);
    return fetchTaskResultWithRetry((id) => this.getRefreshDataResult(analysisId, id), task_id);
  }

  async updatePortfolios(
    analysisId: string,
    request: UpdatePortfoliosRequest
  ): Promise<AnalysisResponse> {
    try {
      const response = await apiClient.put(`/rebalancing/analyses/${analysisId}/portfolios`, request);
      return response.data;
    } catch (error) {
      console.error('Failed to update portfolios:', error);
      throw error;
    }
  }

  async calculateImpact(
    analysisId: string,
    request: CalculateImpactRequest
  ): Promise<RebalancingTaskResponse> {
    try {
      const response = await apiClient.post(`/rebalancing/analyses/${analysisId}/calculate`, request);
      return response.data;
    } catch (error) {
      console.error('Failed to calculate impact:', error);
      throw error;
    }
  }

  async getCalculateImpactResult(analysisId: string, taskId: string): Promise<ImpactCalculationResponse> {
    const response = await apiClient.get(`/rebalancing/analyses/${analysisId}/calculate/${taskId}`);
    return response.data;
  }

  async runCalculateImpactAndWait(
    analysisId: string,
    request: CalculateImpactRequest,
  ): Promise<ImpactCalculationResponse> {
    const { task_id } = await this.calculateImpact(analysisId, request);
    return fetchTaskResultWithRetry((id) => this.getCalculateImpactResult(analysisId, id), task_id);
  }

  // ============= CLEAN SEPARATED API METHODS =============

  async calculateExposuresOnly(
    analysisId: string,
    exposureTypes?: string[]
  ): Promise<RebalancingTaskResponse> {
    try {
      let finalExposureTypes = exposureTypes;
      if (!exposureTypes || exposureTypes.length === 0) {
        const analysis = await this.getAnalysis(analysisId);
        if (analysis.selected_portfolios && analysis.selected_portfolios.length > 0) {
          const firstPortfolio = analysis.selected_portfolios[0];
          const availableExposures = await getAvailableExposures({
            resource_type: "portfolio",
            resource_id: firstPortfolio.portfolio_id,
          });
          finalExposureTypes = availableExposures.exposure_types;
        } else {
          finalExposureTypes = ['asset_allocation', 'geographic_combined', 'equity_sectors'];
        }
      }

      const response = await apiClient.post(`/rebalancing/analyses/${analysisId}/exposures-only`, {
        exposure_types: finalExposureTypes
      });
      return response.data;
    } catch (error) {
      console.error('Failed to calculate exposures:', error);
      throw error;
    }
  }

  async getExposuresOnlyResult(analysisId: string, taskId: string): Promise<any> {
    const response = await apiClient.get(`/rebalancing/analyses/${analysisId}/exposures-only/${taskId}`);
    return response.data;
  }

  async runExposuresOnlyAndWait(analysisId: string, exposureTypes?: string[]): Promise<any> {
    const { task_id } = await this.calculateExposuresOnly(analysisId, exposureTypes);
    return fetchTaskResultWithRetry((id) => this.getExposuresOnlyResult(analysisId, id), task_id);
  }

  async calculateFundamentalsOnly(analysisId: string): Promise<RebalancingTaskResponse> {
    try {
      const response = await apiClient.post(`/rebalancing/analyses/${analysisId}/fundamentals-only`);
      return response.data;
    } catch (error) {
      console.error('Failed to calculate fundamentals:', error);
      throw error;
    }
  }

  async getFundamentalsOnlyResult(analysisId: string, taskId: string): Promise<any> {
    const response = await apiClient.get(`/rebalancing/analyses/${analysisId}/fundamentals-only/${taskId}`);
    return response.data;
  }

  async runFundamentalsOnlyAndWait(analysisId: string): Promise<any> {
    const { task_id } = await this.calculateFundamentalsOnly(analysisId);
    return fetchTaskResultWithRetry((id) => this.getFundamentalsOnlyResult(analysisId, id), task_id);
  }

  async calculateRiskOnly(
    analysisId: string,
    periodMonths: number = 12
  ): Promise<RebalancingTaskResponse> {
    try {
      const response = await apiClient.post(`/rebalancing/analyses/${analysisId}/risk-only`, {
        period_months: periodMonths
      });
      return response.data;
    } catch (error) {
      console.error('Failed to calculate risk:', error);
      throw error;
    }
  }

  async getRiskOnlyResult(analysisId: string, taskId: string): Promise<any> {
    const response = await apiClient.get(`/rebalancing/analyses/${analysisId}/risk-only/${taskId}`);
    return response.data;
  }

  async runRiskOnlyAndWait(analysisId: string, periodMonths: number = 12): Promise<any> {
    const { task_id } = await this.calculateRiskOnly(analysisId, periodMonths);
    return fetchTaskResultWithRetry((id) => this.getRiskOnlyResult(analysisId, id), task_id);
  }

  // ============= END CLEAN API METHODS =============

  async calculateExposureImpact(
    analysisId: string,
    exposureTypes?: string[]
  ): Promise<RebalancingTaskResponse> {
    try {
      let finalExposureTypes = exposureTypes;
      if (!exposureTypes || exposureTypes.length === 0) {
        const analysis = await this.getAnalysis(analysisId);
        if (analysis.selected_portfolios && analysis.selected_portfolios.length > 0) {
          const firstPortfolio = analysis.selected_portfolios[0];
          const availableExposures = await getAvailableExposures({
            resource_type: "portfolio",
            resource_id: firstPortfolio.portfolio_id,
          });
          finalExposureTypes = availableExposures.exposure_types;
        } else {
          finalExposureTypes = ['asset_allocation', 'geographic_combined', 'equity_sectors'];
        }
      }

      const response = await apiClient.post(
        `/rebalancing/analyses/${analysisId}/calculate-exposures`,
        { exposure_types: finalExposureTypes }
      );
      return response.data;
    } catch (error) {
      console.error('Failed to calculate exposure impact:', error);
      throw error;
    }
  }

  async getExposureImpactResult(analysisId: string, taskId: string): Promise<ExposureImpactResponse> {
    const response = await apiClient.get(`/rebalancing/analyses/${analysisId}/calculate-exposures/${taskId}`);
    return response.data;
  }

  async runExposureImpactAndWait(
    analysisId: string,
    exposureTypes?: string[],
  ): Promise<ExposureImpactResponse> {
    const { task_id } = await this.calculateExposureImpact(analysisId, exposureTypes);
    return fetchTaskResultWithRetry((id) => this.getExposureImpactResult(analysisId, id), task_id);
  }

  // Additional calculation endpoints
  async getAnalysisExposures(analysisId: string): Promise<Record<string, any>> {
    try {
      const response = await apiClient.get(`/rebalancing/analyses/${analysisId}/exposures`);
      return response.data;
    } catch (error) {
      console.error('Failed to get exposures:', error);
      throw error;
    }
  }

  async getDriftAnalysis(analysisId: string): Promise<Record<string, any>> {
    try {
      const response = await apiClient.get(`/rebalancing/analyses/${analysisId}/drift`);
      return response.data;
    } catch (error) {
      console.error('Failed to get drift analysis:', error);
      throw error;
    }
  }

  async getPortfolioFees(analysisId: string): Promise<Record<string, any>> {
    try {
      const response = await apiClient.get(`/rebalancing/analyses/${analysisId}/fees`);
      return response.data;
    } catch (error) {
      console.error('Failed to get portfolio fees:', error);
      throw error;
    }
  }

  // Scenario Management Methods
  async listAnalyses(limit: number = 50, offset: number = 0): Promise<AnalysisListResponse> {
    try {
      const response = await apiClient.get(`/rebalancing/analyses/?limit=${limit}&offset=${offset}`);
      return response.data;
    } catch (error) {
      console.error('Failed to list analyses:', error);
      throw error;
    }
  }

  async duplicateAnalysis(analysisId: string, newName: string): Promise<AnalysisResponse> {
    try {
      const response = await apiClient.post(`/rebalancing/analyses/${analysisId}/duplicate`, { name: newName });
      return response.data;
    } catch (error) {
      console.error('Failed to duplicate analysis:', error);
      throw error;
    }
  }

  async deleteAnalysis(analysisId: string): Promise<void> {
    try {
      await apiClient.delete(`/rebalancing/analyses/${analysisId}`);
    } catch (error) {
      console.error('Failed to delete analysis:', error);
      throw error;
    }
  }

  async getDataAudit(analysisId: string, viewMode?: string): Promise<any> {
    try {
      const params = new URLSearchParams();
      if (viewMode) {
        params.append('view_mode', viewMode);
      }
      const url = `/rebalancing/analyses/${analysisId}/data-audit${params.toString() ? `?${params.toString()}` : ''}`;
      const response = await apiClient.get(url);
      return response.data;
    } catch (error) {
      console.error('Failed to get data audit:', error);
      throw error;
    }
  }

  // Toggle between top-level and underlying portfolio views
  async togglePortfolioView(analysisId: string): Promise<AnalysisResponse> {
    try {
      const response = await apiClient.post(`/rebalancing/analyses/${analysisId}/toggle-view`);
      return response.data;
    } catch (error) {
      console.error('Failed to toggle portfolio view:', error);
      throw error;
    }
  }

  // Set view mode to a specific value (top_level, underlying, or aggregate)
  async setViewMode(analysisId: string, viewMode: 'top_level' | 'underlying' | 'aggregate'): Promise<AnalysisResponse> {
    try {
      const response = await apiClient.post(
        `/rebalancing/analyses/${analysisId}/set-view-mode`,
        null,
        { params: { view_mode: viewMode } },
      );
      return response.data;
    } catch (error) {
      console.error('Failed to set view mode:', error);
      throw error;
    }
  }

  async reorderUnderlyingPortfolios(analysisId: string, portfolioIds: string[]): Promise<AnalysisResponse> {
    try {
      const response = await apiClient.put(`/rebalancing/analyses/${analysisId}/underlying-portfolio-order`, {
        portfolio_ids: portfolioIds,
      });
      return response.data;
    } catch (error) {
      console.error('Failed to reorder underlying portfolios:', error);
      throw error;
    }
  }

  // Set comparison portfolio for a specific portfolio in the analysis
  async setComparisonPortfolio(
    analysisId: string,
    portfolioId: string,
    comparisonPortfolioId: string | null,
    lookthrough: boolean = false
  ): Promise<AnalysisResponse> {
    try {
      const params = new URLSearchParams();
      if (comparisonPortfolioId) {
        params.append('comparison_portfolio_id', comparisonPortfolioId);
      }
      params.append('lookthrough', String(lookthrough));
      const response = await apiClient.post(
        `/rebalancing/analyses/${analysisId}/portfolios/${portfolioId}/comparison?${params.toString()}`
      );
      return response.data;
    } catch (error) {
      console.error('Failed to set comparison portfolio:', error);
      throw error;
    }
  }

  /**
   * Lookup ISIN, currency, and long_name for a list of product IDs.
   * Used by export to ensure all products have this data.
   */
  async lookupProductInfo(productIds: string[]): Promise<Record<string, ProductLookupInfo>> {
    try {
      const response = await apiClient.post('/rebalancing/analyses/lookup-product-info', productIds);
      return response.data;
    } catch (error) {
      console.error('Failed to lookup product info:', error);
      return {};
    }
  }

  /**
   * Fetch pre/post cumulative performance series (rebased to 100) for one portfolio.
   */
  async getPerformanceSeries(
    analysisId: string,
    portfolioId: string,
    periodMonths: number = 36,
  ): Promise<{ portfolio_id: string; portfolio_name: string; dates: string[]; series: Record<string, number[]>; period_months: number }> {
    const response = await apiClient.post(`/rebalancing/analyses/${analysisId}/performance-series`, {
      portfolio_id: portfolioId,
      period_months: periodMonths,
    });
    return response.data;
  }

  /**
   * Get client-owned exposure lists (e.g. Saltus Sub Asset Allocation categories)
   * so the FE can render a stable list even when categories have zero exposure.
   */
  async getExposureLists(listIds?: string[]): Promise<Record<string, { name?: string; values: string[] }>> {
    try {
      const params = new URLSearchParams();
      (listIds || []).forEach(id => params.append('list_ids', id));
      const qs = params.toString();
      const url = `/rebalancing/exposure-lists/${qs ? `?${qs}` : ''}`;
      const response = await apiClient.get(url);
      return response.data?.lists || {};
    } catch (error) {
      console.error('Failed to get exposure lists:', error);
      return {};
    }
  }

}

function portfolioListKey(
  viewMode: AnalysisResponse['view_mode'],
): 'selected_portfolios' | 'underlying_portfolios' | 'aggregate_portfolios' {
  if (viewMode === 'underlying') return 'underlying_portfolios';
  if (viewMode === 'aggregate') return 'aggregate_portfolios';
  return 'selected_portfolios';
}

const emptyPortfolioLists = {
  selected_portfolios: [] as PortfolioSelectionResponse[],
  underlying_portfolios: [] as PortfolioSelectionResponse[],
  aggregate_portfolios: [] as PortfolioSelectionResponse[],
};

/** Keep only the active view's portfolios in client state (avoids showing two views at once). */
export function applyViewModeToAnalysis(
  analysis: AnalysisResponse,
  viewMode: AnalysisResponse['view_mode'],
  activePortfolios: PortfolioSelectionResponse[],
): AnalysisResponse {
  const mode = viewMode || 'top_level';
  const key = portfolioListKey(mode);
  return {
    ...analysis,
    view_mode: mode,
    ...emptyPortfolioLists,
    [key]: activePortfolios,
  };
}

export function viewPortfoliosNeedHydration(
  analysis: AnalysisResponse,
  viewMode?: AnalysisResponse['view_mode'],
): boolean {
  const mode = viewMode || analysis.view_mode || 'top_level';
  const key = portfolioListKey(mode);
  const portfolios = analysis[key] || [];

  if (portfolios.length === 0) {
    if (mode === 'aggregate') {
      return true;
    }
    if (mode === 'top_level' && analysis.has_underlying_portfolios) {
      return true;
    }
    if (mode === 'underlying' && analysis.has_underlying_portfolios) {
      return true;
    }
    return false;
  }

  return portfolios.some((p) => !p.holdings || p.holdings.length === 0);
}

export async function hydrateAnalysisViewPortfolios(
  analysis: AnalysisResponse,
  viewMode?: AnalysisResponse['view_mode'],
): Promise<AnalysisResponse> {
  const mode = viewMode || analysis.view_mode || 'top_level';
  const portfolios = await rebalancingApi.getAnalysisPortfolios(analysis.analysis_id, mode);
  return applyViewModeToAnalysis(analysis, mode, portfolios);
}

/** Drop inactive view portfolio payloads after a full AnalysisResponse from the API. */
export function syncAnalysisToViewMode(analysis: AnalysisResponse): AnalysisResponse {
  const mode = analysis.view_mode || 'top_level';
  const key = portfolioListKey(mode);
  return applyViewModeToAnalysis(analysis, mode, analysis[key] || []);
}

export const rebalancingApi = new RebalancingApiService();